import React, { useMemo, useState } from 'react'
import styled from '@emotion/styled'
import useSponsor from '@Hooks/useSponsor'
import { DragEndArgs, useDraggableTable } from '@Reusable/DragAndDropTableComponents/Hooks/useDraggableTable'
import { Button, Divider, message, Popconfirm, Space, Table, Tag } from 'antd'

import LeadScanQuestionDrawer from './Components/LeadScanQuestionDrawer'
import useLeadScanQuestions from './Hooks/useLeadScanQuestions'
import { LeadScanQuestion, LeadScanQuestionFieldType } from './interfaces'

export default function LeadScanQuestions() {
  const { sponsor: { id: sponsorId } } = useSponsor()
  const { leadScanQuestions, remove, update } = useLeadScanQuestions({ sponsorId })
  const [drawerState, setDrawerState] = useState<{ visible: boolean, selectedLeadScanQuestion: LeadScanQuestion | null }>({
    visible: false,
    selectedLeadScanQuestion: null
  })

  const leadScanQuestionsWithFixed = useMemo(() => {
    const fixedQuestion: LeadScanQuestion = {
      type: 'lead-scan-question' as const,
      id: 'fixed',
      fieldType: 'Text' as const,
      options: [],
      title: 'Add notes (Default question)'
    }

    return [
      fixedQuestion,
      ...leadScanQuestions
    ]
  }, [leadScanQuestions])

  const handleReorder = ({ newIndex, item, revert }: DragEndArgs<LeadScanQuestion>) => {
    update.mutate({
      id: item.id,
      data: {
        question: {
          // Backend expects 1-based positions
          position: newIndex + 1
        }
      }
    }, {
      onError: revert,
      onSuccess: () => {
        message.success('Position saved')
      }
    })
  }

  const fixedRowCount = leadScanQuestionsWithFixed.filter((q) => q.id === 'fixed').length
  const { tableProps, DragHandle } = useDraggableTable<LeadScanQuestion>(leadScanQuestionsWithFixed, { fixedRowCount, onDragEnd: handleReorder })

  return (
    <>
      <Header>
        <Button
          onClick={() => setDrawerState({ visible: true, selectedLeadScanQuestion: null })}
          type={'primary'}
        >
          {'Create new'}
        </Button>
      </Header>
      <Table
        loading={remove.isLoading}
        rowKey={'id'}
        {...tableProps}
        columns={[
          { key: 'sort', align: 'center', width: 80, render: () => <DragHandle /> },
          {
            title: 'Type',
            dataIndex: 'fieldType',
            key: 'fieldType',
            width: 120,
            render: (_, { fieldType }) => <Tag>{FIELD_TYPES[fieldType]?.label || fieldType}</Tag>
          },
          {
            title: 'Question',
            dataIndex: 'title',
            key: 'title'
          },
          {
            title: 'Actions',
            key: 'actions',
            width: 160,
            render: (_, leadScanQuestion) => {
              const isFixed = leadScanQuestion.id === 'fixed'

              return (
                <Space>
                  <LinkButton
                    disabled={isFixed}
                    onClick={() => setDrawerState({ selectedLeadScanQuestion: leadScanQuestion, visible: true })}
                    type={'link'}
                  >
                    {'Edit'}
                  </LinkButton>
                  <Divider type={'vertical'} />
                  <Popconfirm
                    title={'Are you sure you want to remove this question?'}
                    onConfirm={() => remove.mutate({ id: leadScanQuestion.id })}
                    okText={'Remove'}
                    okButtonProps={{ danger: true }}
                  >
                    <LinkButton
                      danger
                      disabled={isFixed}
                      type={'link'}
                    >
                      {'Remove'}
                    </LinkButton>
                  </Popconfirm>
                </Space>
              )
            }
          }
        ]}
        bordered
        pagination={false}
      />
      <LeadScanQuestionDrawer
        key={drawerState.selectedLeadScanQuestion?.id || 'new'}
        visible={drawerState.visible}
        onClose={() => setDrawerState({ visible: false, selectedLeadScanQuestion: null })}
        leadScanQuestion={drawerState.selectedLeadScanQuestion}
      />
    </>
  )
}

const Header = styled.div(({ theme }) => `
  display: flex;
  justify-content: flex-end;
  margin-block: ${theme.margins.baseMargin}px;
`)

const LinkButton = styled(Button)`
  padding: 0 !important;
`

export const FIELD_TYPES: Record<LeadScanQuestionFieldType, { label: string, hasOptions: boolean }> = {
  Text: {
    label: 'Text',
    hasOptions: false
  },
  SingleChoice: {
    label: 'Single select',
    hasOptions: true
  },
  MultipleChoice: {
    label: 'Multiple select',
    hasOptions: true
  }
}
